// WebSocket provider. Mounts one WsClient per authenticated session and
// exposes it via useWsClient(). Also subscribes to ws:{tenant}:alerts so
// AppShell can show a badge without every screen having to wire this up.
//
// Lifecycle: client connects on mount (once auth is ready), disconnects on
// unmount or logout. Reconnect backoff lives inside WsClient.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";

import { useAuth } from "@/lib/auth/AuthProvider";
import { WsClient } from "@/lib/ws/wsClient";

type WsLike = Pick<WsClient, "connect" | "close" | "subscribe">;

interface WsContextValue {
  client: WsLike | null;
  // Number of unacknowledged alert payloads received this session.
  unreadAlerts: number;
  clearAlertBadge: () => void;
}

const WsContext = createContext<WsContextValue>({
  client: null,
  unreadAlerts: 0,
  clearAlertBadge: () => void 0,
});

export function WsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [client, setClient] = useState<WsLike | null>(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  useEffect(() => {
    if (!user) {
      setClient(null);
      return;
    }
    const ws: WsLike = new WsClient(user.tenantId);
    ws.connect();
    // Flush the live client to consumers so live-push effects can re-arm.
    setClient(ws);

    // Pre-wire the alerts channel so the bell badge works from session start.
    const unsub = ws.subscribe(`ws:${user.tenantId}:alerts`, () => {
      setUnreadAlerts((n) => n + 1);
    });

    return () => {
      unsub();
      ws.close();
      setClient(null);
    };
  }, [user]);

  const clearAlertBadge = useCallback(() => setUnreadAlerts(0), []);

  return (
    <WsContext.Provider
      value={{ client, unreadAlerts, clearAlertBadge }}
    >
      {children}
    </WsContext.Provider>
  );
}

export function useWsClient(): WsContextValue {
  return useContext(WsContext);
}
